const DESKTOP_QUERY = window.matchMedia('(min-width: 990px)');
const SEARCH_DEBOUNCE_MS = 250;

/**
 * Creates an element with a class name and optional text content.
 * @param {string} tag
 * @param {string} className
 * @param {string} [text]
 * @returns {HTMLElement}
 */
function createElement(tag, className, text) {
  const element = document.createElement(tag);
  element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}

/**
 * Formats a predictive search price string using the active storefront currency.
 * @param {string | number} price
 * @returns {string}
 */
function formatPrice(price) {
  const currency = window.Shopify?.currency?.active || 'MXN';
  const amount = Number(price);
  if (Number.isNaN(amount)) return String(price);
  const formatted = new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
  }).format(amount);
  return `${formatted} ${currency}`;
}

/**
 * Mr. Brown header: keeps header height CSS variables in sync, rotates the mobile
 * announcement bar and toggles the mobile menu panel.
 */
class MrbHeader extends HTMLElement {
  #resizeObserver = new ResizeObserver(() => this.#updateHeights());
  #rotateTimer = 0;
  #searchTimer = 0;
  /** @type {AbortController | null} */
  #searchAbort = null;
  #emptyStateHTML = '';

  connectedCallback() {
    this.#resizeObserver.observe(this);
    this.#updateHeights();
    this.#startRotation();
    this.#emptyStateHTML = this.searchPanel?.querySelector('[data-search-results]')?.innerHTML ?? '';

    this.addEventListener('input', this.#handleSearchInput);
    this.addEventListener('focusin', this.#handleSearchFocus);
    this.searchPanel?.addEventListener('cancel', this.#handlePanelCancel);
    this.searchPanel?.addEventListener('click', this.#handlePanelBackdrop);
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('pointerover', this.#resetClosedItems);
    this.addEventListener('pointerleave', this.#resetClosedItems);
    this.addEventListener('focusin', this.#resetClosedItems);
    document.addEventListener('click', this.#handleDocumentClick);
    document.addEventListener('keydown', this.#handleKeydown);
    DESKTOP_QUERY.addEventListener('change', this.#handleBreakpoint);
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
    window.clearInterval(this.#rotateTimer);
    window.clearTimeout(this.#searchTimer);
    this.#searchAbort?.abort();
    this.removeEventListener('input', this.#handleSearchInput);
    this.removeEventListener('focusin', this.#handleSearchFocus);
    this.searchPanel?.removeEventListener('cancel', this.#handlePanelCancel);
    this.searchPanel?.removeEventListener('click', this.#handlePanelBackdrop);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('pointerover', this.#resetClosedItems);
    this.removeEventListener('pointerleave', this.#resetClosedItems);
    this.removeEventListener('focusin', this.#resetClosedItems);
    document.removeEventListener('click', this.#handleDocumentClick);
    document.removeEventListener('keydown', this.#handleKeydown);
    DESKTOP_QUERY.removeEventListener('change', this.#handleBreakpoint);
    document.body.classList.remove('mrb-scroll-lock');
  }

  get menuToggle() {
    return this.querySelector('[data-menu-toggle]');
  }

  get mobileMenu() {
    return this.querySelector('[data-mobile-menu]');
  }

  /** @returns {HTMLDialogElement | null} */
  get searchPanel() {
    return this.querySelector('dialog[data-search-panel]');
  }

  openSearch() {
    const panel = this.searchPanel;
    if (!panel || panel.open) return;

    this.toggleMenu(false);
    panel.showModal();
    document.body.classList.add('mrb-scroll-lock');
    requestAnimationFrame(() => panel.classList.add('is-open'));
    panel.querySelector('input[type="search"]')?.focus();
  }

  closeSearch() {
    const panel = this.searchPanel;
    if (!panel?.open) return;

    panel.classList.remove('is-open');
    document.body.classList.remove('mrb-scroll-lock');
    const finish = () => panel.open && panel.close();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
    } else {
      panel.addEventListener('transitionend', finish, { once: true });
      window.setTimeout(finish, 400);
    }
  }

  /** @param {Event} event */
  #handlePanelCancel = (event) => {
    event.preventDefault();
    this.closeSearch();
  };

  /** @param {MouseEvent} event */
  #handlePanelBackdrop = (event) => {
    if (event.target === this.searchPanel) this.closeSearch();
  };

  /**
   * @param {Element} input
   * @returns {HTMLElement | null}
   */
  #resultsFor(input) {
    const form = input.closest('[data-search-form]');
    return form?.querySelector('[data-search-results]') ?? form?.closest('dialog')?.querySelector('[data-search-results]') ?? null;
  }

  /** @param {Event} event */
  #handleSearchInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-search-input]')) return;

    window.clearTimeout(this.#searchTimer);
    this.#searchTimer = window.setTimeout(() => this.#search(input), SEARCH_DEBOUNCE_MS);
  };

  /** @param {FocusEvent} event */
  #handleSearchFocus = (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.matches('.mrb-header__search-input') && input.value.trim()) {
      this.#search(input);
    }
  };

  /** @param {HTMLInputElement} input */
  async #search(input) {
    const results = this.#resultsFor(input);
    if (!results) return;

    const isDropdown = !results.closest('dialog');
    const term = input.value.trim();
    const minChars = Number(this.dataset.searchMin) || 2;

    this.#searchAbort?.abort();

    if (term.length < minChars) {
      if (isDropdown) {
        results.hidden = true;
        results.replaceChildren();
      } else {
        results.innerHTML = this.#emptyStateHTML;
      }
      return;
    }

    results.hidden = false;
    results.replaceChildren(createElement('p', 'mrb-search-results__status', this.dataset.textLoading || ''));

    const abort = new AbortController();
    this.#searchAbort = abort;

    try {
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product',
        'resources[limit]': '8',
        'resources[options][unavailable_products]': 'last',
      });
      const response = await fetch(`${this.dataset.suggestUrl}?${params}`, { signal: abort.signal });
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

      const data = await response.json();
      this.#renderResults(results, data?.resources?.results?.products ?? [], term);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
      this.#renderResults(results, [], term);
    }
  }

  /**
   * @param {HTMLElement} container
   * @param {Array<{ title: string, url: string, price: string, featured_image?: { url?: string, alt?: string }, image?: string, vendor?: string }>} products
   * @param {string} term
   */
  #renderResults(container, products, term) {
    const searchUrl = `${this.dataset.searchUrl}?${new URLSearchParams({ q: term, 'options[prefix]': 'last' })}`;

    if (products.length === 0) {
      const empty = createElement('div', 'mrb-search-empty');
      empty.append(createElement('p', 'mrb-search-empty__title', `${this.dataset.textNoResults || ''} “${term}”`));
      container.replaceChildren(empty);
      return;
    }

    const list = createElement('ul', 'mrb-search-results');
    list.setAttribute('role', 'list');

    for (const product of products) {
      const item = document.createElement('li');
      const link = /** @type {HTMLAnchorElement} */ (createElement('a', 'mrb-search-result'));
      link.href = product.url;

      const imageUrl = product.featured_image?.url || product.image;
      const media = createElement('span', 'mrb-search-result__media');
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl.includes('?') ? `${imageUrl}&width=120` : `${imageUrl}?width=120`;
        image.alt = product.featured_image?.alt || product.title;
        image.width = 56;
        image.height = 56;
        image.loading = 'lazy';
        media.append(image);
      }

      const info = createElement('span', 'mrb-search-result__info');
      if (product.vendor) info.append(createElement('span', 'mrb-search-result__vendor', product.vendor));
      info.append(createElement('span', 'mrb-search-result__title', product.title));
      info.append(createElement('span', 'mrb-search-result__price', formatPrice(product.price)));

      link.append(media, info);
      item.append(link);
      list.append(item);
    }

    const viewAll = /** @type {HTMLAnchorElement} */ (
      createElement('a', 'mrb-search-results__all', this.dataset.textViewAll || '')
    );
    viewAll.href = searchUrl;

    container.replaceChildren(list, viewAll);
  }

  #hideDropdowns() {
    for (const dropdown of this.querySelectorAll('.mrb-header__search-dropdown')) {
      if (dropdown instanceof HTMLElement) dropdown.hidden = true;
    }
  }

  #updateHeights() {
    const height = this.offsetHeight;
    const headerGroup = document.getElementById('header-group');
    let groupHeight = 0;

    if (headerGroup) {
      for (const child of headerGroup.children) {
        if (child instanceof HTMLElement) groupHeight += child.offsetHeight;
      }
    }

    document.body.style.setProperty('--header-height', `${height}px`);
    document.body.style.setProperty('--header-group-height', `${groupHeight || height}px`);
    this.style.setProperty('--mrb-header-bottom', `${Math.max(this.getBoundingClientRect().bottom, 0)}px`);
  }

  #startRotation() {
    const items = this.querySelectorAll('[data-announce-item]');
    const speed = Number(this.dataset.rotateSpeed) || 5000;
    if (items.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let index = 0;
    this.#rotateTimer = window.setInterval(() => {
      if (DESKTOP_QUERY.matches || window.matchMedia('(min-width: 750px)').matches) return;
      items[index]?.classList.remove('is-active');
      index = (index + 1) % items.length;
      items[index]?.classList.add('is-active');
    }, speed);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-menu-toggle]')) {
      this.toggleMenu();
      return;
    }

    if (target.closest('[data-search-open]')) {
      this.openSearch();
      return;
    }

    if (target.closest('[data-search-close]')) {
      this.closeSearch();
      return;
    }

    const navLink = target.closest('.mrb-nav__item--has-children > .mrb-nav__link');
    if (navLink && window.matchMedia('(hover: none)').matches) {
      const item = navLink.parentElement;
      if (item && !item.classList.contains('is-open')) {
        event.preventDefault();
        this.#closeMegaMenus();
        item.classList.add('is-open');
      }
    }
  };

  #closeMegaMenus() {
    for (const item of this.querySelectorAll('.mrb-nav__item.is-open')) {
      item.classList.remove('is-open');
    }
  }

  /** @param {MouseEvent} event */
  #handleDocumentClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!this.contains(target)) this.#closeMegaMenus();
    if (!target.closest('.mrb-header__search')) this.#hideDropdowns();
  };

  /** @param {Event} event */
  #resetClosedItems = (event) => {
    const target = event.type === 'pointerleave' ? null : event.target;
    for (const item of this.querySelectorAll('.mrb-nav__item.is-closed')) {
      if (!(target instanceof Node) || !item.contains(target)) item.classList.remove('is-closed');
    }
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key === 'Escape') {
      this.#hideDropdowns();
      const openItem = document.activeElement?.closest('.mrb-nav__item--has-children') ?? this.querySelector('.mrb-nav__item--has-children:hover');
      if (openItem instanceof HTMLElement && this.contains(openItem)) {
        this.#closeMegaMenus();
        openItem.classList.add('is-closed');
        openItem.querySelector('.mrb-nav__link')?.focus();
      }
    }

    if (event.key === 'Escape' && this.menuToggle?.getAttribute('aria-expanded') === 'true') {
      this.toggleMenu(false);
      this.menuToggle?.focus();
    }
  };

  #handleBreakpoint = () => {
    if (DESKTOP_QUERY.matches) this.toggleMenu(false);
  };

  /** @param {boolean} [force] */
  toggleMenu(force) {
    const toggle = this.menuToggle;
    const menu = this.mobileMenu;
    if (!toggle || !menu) return;

    const open = force ?? toggle.getAttribute('aria-expanded') !== 'true';
    if (open) this.#updateHeights();
    toggle.setAttribute('aria-expanded', String(open));
    menu.hidden = !open;
    document.body.classList.toggle('mrb-scroll-lock', open);
  }
}

if (!customElements.get('mrb-header')) {
  customElements.define('mrb-header', MrbHeader);
}
